import React, { useEffect, useMemo, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  BsBarChartLine,
  BsBell,
  BsBoxArrowRight,
  BsBoxSeam,
  BsChatDots,
  BsChevronLeft,
  BsChevronRight,
  BsCpu,
  BsGear,
  BsGlobe2,
  BsHeadset,
  BsHouseDoor,
  BsX,
} from 'react-icons/bs';
import api from '../../lib/api';

export default function UserSidebar({
  collapsed,
  setCollapsed,
  mobileSidebarOpen,
  setMobileSidebarOpen,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const isAgent = useSelector((s) => !!s.auth.is_agent);
  const ownsWorkspace = useSelector((s) => !!s.auth.owns_workspace);
  const [handoverUnread, setHandoverUnread] = useState(0);

  const handoverOnlyAgent = isAgent && !ownsWorkspace;

  useEffect(() => {
    let mounted = true;

    const loadUnread = async () => {
      try {
        const { data } = await api.get('/handover/notifications/unread-count/', {
          suppressToast: true,
        });

        if (mounted) {
          setHandoverUnread(Number(data?.unread_count || 0));
        }
      } catch {
        if (mounted) {
          setHandoverUnread(0);
        }
      }
    };

    loadUnread();
    const timer = setInterval(loadUnread, 15000);

    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, []);

  const ownerLinks = useMemo(() => ([
    { to: '/dashboard', label: 'Overview', icon: BsHouseDoor },
    { to: '/dashboard/widgets', label: 'My Widgets', icon: BsBoxSeam },
    { to: '/dashboard/training', label: 'Training Data', icon: BsGlobe2 },
    { to: '/dashboard/channels', label: 'Channels', icon: BsCpu },
    { to: '/dashboard/history', label: 'Chat History', icon: BsChatDots },
    { to: '/dashboard/agent-inbox', label: 'Agent Inbox', icon: BsHeadset },
    { to: '/dashboard/agent-analytics', label: 'Agent Analytics', icon: BsBarChartLine },
    { to: '/dashboard/notifications', label: 'Notifications', icon: BsBell },
    { to: '/dashboard/settings', label: 'Settings', icon: BsGear },
    { to: '/dashboard/analytics', label: 'Analytics', icon: BsBarChartLine },
  ]), []);

  const agentLinks = useMemo(() => ([
    { to: '/dashboard/agent-inbox', label: 'Agent Inbox', icon: BsHeadset },
    { to: '/dashboard/agent-analytics', label: 'Agent Analytics', icon: BsBarChartLine },
    { to: '/dashboard/notifications', label: 'Notifications', icon: BsBell },
  ]), []);

  const links = handoverOnlyAgent ? agentLinks : ownerLinks;

  const handleLogout = () => {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('admin_pin_ok');
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }

    return location.pathname === path || location.pathname.startsWith(path);
  };

  return (
    <aside
      className={`user-sidebar admin-sidebar ${collapsed ? 'collapsed' : ''} ${
        mobileSidebarOpen ? 'mobile-open' : ''
      }`}
    >
      <div className="user-sidebar__header admin-sidebar__header">
        <div className="user-sidebar__brand admin-sidebar__brand">
          {!collapsed && (
            <div>
              <h6 className="mb-0 fw-bold text-white">
                {handoverOnlyAgent ? 'Agent Portal' : 'AI Dashboard'}
              </h6>
              <small className="text-white-50">
                {handoverOnlyAgent ? 'Support Console' : 'User Portal'}
              </small>
            </div>
          )}
        </div>

        <button
          type="button"
          className="user-sidebar__toggle admin-sidebar__toggle d-none d-lg-grid"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <BsChevronRight /> : <BsChevronLeft />}
        </button>

        <button
          type="button"
          className="user-sidebar__toggle admin-sidebar__toggle d-grid d-lg-none"
          onClick={() => setMobileSidebarOpen(false)}
          aria-label="Close sidebar"
        >
          <BsX />
        </button>
      </div>

      <div className="user-sidebar__menu admin-sidebar__menu">
        <Nav className="flex-column gap-1">
          {links.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            const shouldShowUnread =
              item.to === '/dashboard/agent-inbox' ||
              item.to === '/dashboard/notifications';

            return (
              <Nav.Item key={item.to}>
                <Link
                  to={item.to}
                  className={`user-sidebar__link admin-sidebar__link ${active ? 'active' : ''}`}
                  onClick={() => setMobileSidebarOpen(false)}
                >
                  <span className="user-sidebar__icon admin-sidebar__icon">
                    <Icon />
                  </span>

                  {!collapsed && (
                    <span className="d-flex align-items-center gap-2">
                      <span>{item.label}</span>
                      {shouldShowUnread && handoverUnread > 0 ? (
                        <span className="badge rounded-pill bg-danger">
                          {handoverUnread}
                        </span>
                      ) : null}
                    </span>
                  )}
                </Link>
              </Nav.Item>
            );
          })}
        </Nav>
      </div>

      <div className="user-sidebar__footer admin-sidebar__footer">
        <button
          type="button"
          className="user-sidebar__logout admin-sidebar__logout"
          onClick={handleLogout}
        >
          <span className="user-sidebar__icon admin-sidebar__icon">
            <BsBoxArrowRight />
          </span>
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
}